// Cellery Soapbox — matchMedia React hook + responsive convenience hooks.
//
// Drives the JS-side responsive branches that pure CSS can't express: the
// Doc/Comments segmented control, the workspace/slide drawers, and the
// toolbar "More" menu. Layout that CSS can handle stays in `theme.css`.

import { useEffect, useState } from "react";
import {
  COARSE_POINTER_QUERY,
  COMPACT_TOOLBAR_QUERY,
  ONE_COLUMN_QUERY,
  PHONE_QUERY,
} from "./breakpoints";

/**
 * Subscribe to a CSS media query. SSR-safe: returns `false` until mounted,
 * mirroring the `window.matchMedia?.` guard used elsewhere in the app.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    // Re-sync immediately in case the query changed between render and effect.
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * True when the viewer toolbar must economize on width (≤1100px): secondary
 * actions render icon-only (label hidden, aria-label + tooltip retained).
 */
export const useIsCompactToolbar = () => useMediaQuery(COMPACT_TOOLBAR_QUERY);

/** True when the viewer/workspace collapse to a single column (≤860px). */
export const useIsSingleColumn = () => useMediaQuery(ONE_COLUMN_QUERY);

/** True at phone scale (≤600px) — drives condensed chrome + drawers. */
export const useIsPhone = () => useMediaQuery(PHONE_QUERY);

/** True on touch / no-hover devices — reveals hidden affordances. */
export const useCoarsePointer = () => useMediaQuery(COARSE_POINTER_QUERY);
